import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import NumberInput from '../../../Components/NumberInput';
import { DateInterval, DateIntervalUnit } from '../../../Helpers/DateUtils';
import { INTERVAL_UNITS } from '../../BuildingChecks/types';
import './AssessmentScheduleEditors.css';

/** Schedule editors for the Skickbedömning section of `ActivatePlanModal`. */

interface IntervalEditorProps {
    interval: DateInterval;
    disabled: boolean;
    onCommit: (interval: DateInterval) => void;
}

/** Number + unit editor that commits the number on blur and the unit at once. */
export const IntervalEditor: React.FC<IntervalEditorProps> = ({ interval, disabled, onCommit }) => {
    const [value, setValue] = useState<number>(interval.value);

    useEffect(() => {
        setValue(interval.value);
    }, [interval.value]);

    const commitValue = () => {
        const next = Math.max(1, Math.round(value || 1));
        if (next !== interval.value) {
            onCommit({ value: next, unit: interval.unit });
        } else if (next !== value) {
            setValue(next);
        }
    };

    return (
        <div className="assessment-interval-editor">
            <NumberInput
                className="form-control form-control-sm"
                min="1"
                integer
                emptyValue={1}
                value={value}
                disabled={disabled}
                onChange={setValue}
                onBlur={commitValue}
            />
            <Form.Select
                size="sm"
                value={interval.unit}
                disabled={disabled}
                onChange={e => onCommit({ value: Math.max(1, value || 1), unit: e.target.value as DateIntervalUnit })}
            >
                {INTERVAL_UNITS.map(unit => (
                    <option key={unit.value} value={unit.value}>{unit.label}</option>
                ))}
            </Form.Select>
        </div>
    );
};

interface DueDateEditorProps {
    value: string;
    disabled: boolean;
    onCommit: (date: string) => void;
}

/**
 * Date input that commits on blur or Enter, never on every keystroke: browsers fire
 * `change` for each valid intermediate value while a year is typed (0002-…, 0020-…),
 * and each commit recomputes the activation schedule.
 */
export const DueDateEditor: React.FC<DueDateEditorProps> = ({ value, disabled, onCommit }) => {
    const [draft, setDraft] = useState(value);

    useEffect(() => {
        setDraft(value);
    }, [value]);

    const commit = () => {
        if (draft && /^\d{4}-\d{2}-\d{2}$/.test(draft) && draft >= '2000-01-01' && draft !== value) {
            onCommit(draft);
        } else if (draft !== value) {
            setDraft(value);
        }
    };

    return (
        <Form.Control
            type="date"
            size="sm"
            min="2000-01-01"
            value={draft}
            disabled={disabled}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={e => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
        />
    );
};
